import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Public pages — loaded immediately
import Public from './pages/Public';
import CroatianPublic from './pages/CroatianPublic';
import SerbianPublic from './pages/SerbianPublic';
import { trackPageView } from './utils/metaPixel';

// Everything else — lazy loaded (only when navigated to)
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Admin = lazy(() => import('./pages/Admin'));
const Navbar = lazy(() => import('./components/Navbar'));
const Landing = lazy(() => import('./pages/Landing'));
const Create = lazy(() => import('./pages/Create'));
const Generating = lazy(() => import('./pages/Generating'));
const SongResult = lazy(() => import('./pages/SongResult'));
const Library = lazy(() => import('./pages/Library'));
const SharedSong = lazy(() => import('./pages/SharedSong'));
const VideoCreator = lazy(() => import('./pages/VideoCreator'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CreateSong = lazy(() => import('./pages/CreateSong'));
const LyricsReview = lazy(() => import('./pages/LyricsReview'));
const SongPreview = lazy(() => import('./pages/SongPreview'));
const PublicGenerating = lazy(() => import('./pages/PublicGenerating'));

// Fire PageView on every SPA route change
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => { trackPageView(); }, [location.pathname]);
  return null;
}

// Admin layout with dark navbar
function AdminLayout({ children }) {
  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Navbar />
      {children}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <PageViewTracker />
            <Suspense fallback={<div style={{minHeight:'100vh'}} />}>
              <Routes>
                {/* Public customer pages — white + gold */}
                <Route path="/" element={<Public />} />
                <Route path="/en" element={<Public />} />
                <Route path="/hr" element={<CroatianPublic />} />
                <Route path="/rs" element={<SerbianPublic />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/share/:slug" element={<SharedSong />} />

                {/* Public self-serve flow (Suno-style: free create, pay to download) */}
                <Route path="/create" element={<CreateSong />} />
                <Route path="/lyrics/:id" element={<LyricsReview />} />
                <Route path="/generating/:jobId" element={<PublicGenerating />} />
                <Route path="/song/:id" element={<SongPreview />} />

                {/* Order management dashboard */}
                <Route path="/dashboard" element={<Dashboard />} />

                {/* Admin pages — dark theme, existing creator tools */}
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/create" element={<AdminLayout><Create /></AdminLayout>} />
                <Route path="/admin/generating/:jobId" element={<AdminLayout><Generating /></AdminLayout>} />
                <Route path="/admin/song/:id" element={<AdminLayout><SongResult /></AdminLayout>} />
                <Route path="/admin/library" element={<AdminLayout><Library /></AdminLayout>} />
                <Route path="/admin/video" element={<AdminLayout><VideoCreator /></AdminLayout>} />
                <Route path="/admin/home" element={<AdminLayout><Landing /></AdminLayout>} />

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
